import * as THREE from 'three';
import type { WebGPUBundleGroupCtor } from './Renderer';

/**
 * Wraps a set of mostly-static meshes so the WebGPU backend records their
 * draw commands once into a `GPURenderBundle` and replays them each frame
 * with one CPU-cheap call. On WebGL2 (and any other backend) this is a
 * plain `THREE.Group` — the scene topology is identical, but no perf
 * benefit is available because WebGL2 has no equivalent primitive.
 *
 * Per the Three docs (`renderers/common/BundleGroup.js`): the `static` flag
 * is a hint that the structure (children, geometry/material identity, draw
 * count) is stable. Per-frame uniform writes are NOT part of the bundle
 * recording, so animated colors / time-driven shaders / shadow updates all
 * keep working without invalidation. Things that DO require an explicit
 * `invalidate()`:
 *  - Adding or removing a child mesh
 *  - Swapping a material instance (different node graph)
 *  - Replacing or resizing geometry attributes
 *  - Toggling a child's `.visible`
 */
export interface StaticBundleHandle {
  /**
   * Add static meshes to this group instead of the scene.
   * Type is `THREE.Group` — the BundleGroup subclass exists in `three/webgpu`
   * but consumers don't need to know which backend they got.
   */
  readonly group: THREE.Group;
  /**
   * Mark the bundle dirty so the renderer re-records its draw calls on the
   * next render. Cheap (a version bump on the WebGPU path; no-op on WebGL).
   * Call this when a child's geometry/material/visibility changes.
   */
  invalidate(): void;
}

export function createStaticBundle(
  bundleGroupCtor: WebGPUBundleGroupCtor | null,
): StaticBundleHandle {
  if (bundleGroupCtor) {
    const bundle = new bundleGroupCtor();
    bundle.static = true;
    return {
      group: bundle,
      invalidate: (): void => { bundle.needsUpdate = true; },
    };
  }
  const group = new THREE.Group();
  return {
    group,
    invalidate: (): void => {},
  };
}
